import type { GatheringRsvp, RsvpStatus } from "@/lib/clubs/gatherings";
import styles from "./RsvpBreakdown.module.scss";

interface Props {
  rsvps: GatheringRsvp[];
}

const COLUMNS: { status: RsvpStatus; label: string }[] = [
  { status: "GOING", label: "Going" },
  { status: "MAYBE", label: "Maybe" },
  { status: "CANT", label: "Can’t make it" },
];

/**
 * Member-RSVP rows (`userId` non-null) are grouped by `status`. Guest RSVPs
 * (`userId` null, created via the public RSVP form) have `status: null` —
 * they are treated as confirmed-going and rendered in the GOING column with
 * a "Guest" pill, matching the issue #296 spec.
 */
function isGuest(r: GatheringRsvp): boolean {
  return r.userId === null;
}

function isActive(r: GatheringRsvp): boolean {
  return r.cancelledAt === null;
}

export function RsvpBreakdown({ rsvps }: Props) {
  const grouped: Record<RsvpStatus, GatheringRsvp[]> = {
    GOING: [],
    MAYBE: [],
    CANT: [],
  };
  let guestCount = 0;
  let memberGoingCount = 0;
  let memberMaybeCount = 0;
  let memberCantCount = 0;

  for (const r of rsvps) {
    if (!isActive(r)) continue;
    if (isGuest(r)) {
      // Public RSVPs default to going (no MAYBE/CANT path on the public form).
      grouped.GOING.push(r);
      guestCount += 1;
      continue;
    }
    const status = r.status ?? "GOING";
    grouped[status].push(r);
    if (status === "GOING") memberGoingCount += 1;
    else if (status === "MAYBE") memberMaybeCount += 1;
    else if (status === "CANT") memberCantCount += 1;
  }

  const totalAttending = memberGoingCount + guestCount;

  return (
    <section className={styles.section} aria-labelledby="rsvp-heading">
      <h2 id="rsvp-heading" className={styles.heading}>
        RSVPs
      </h2>
      <p className={styles.summary}>
        <span className={styles.summaryTotal}>{totalAttending} attending</span>
        <span className={styles.summaryBreakdown}>
          {memberGoingCount} member{memberGoingCount === 1 ? "" : "s"} ·{" "}
          {guestCount} guest{guestCount === 1 ? "" : "s"}
          {memberMaybeCount > 0 ? ` · ${memberMaybeCount} maybe` : ""}
          {memberCantCount > 0 ? ` · ${memberCantCount} can’t make it` : ""}
        </span>
      </p>
      <div className={styles.grid}>
        {COLUMNS.map((col) => {
          const list = grouped[col.status];
          return (
            <div key={col.status} className={styles.column}>
              <header className={styles.colHead}>
                <span className={styles.colLabel}>{col.label}</span>
                <span className={styles.colCount}>{list.length}</span>
              </header>
              {list.length === 0 ? (
                <p className={styles.empty}>No one yet.</p>
              ) : (
                <ul className={styles.list}>
                  {list.map((r) => (
                    <RsvpRow key={r.id} rsvp={r} />
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function RsvpRow({ rsvp }: { rsvp: GatheringRsvp }) {
  const guest = isGuest(rsvp);
  const displayName = guest
    ? (rsvp.name ?? "Guest")
    : (rsvp.user?.displayName ?? "Member");
  const initial = (displayName ?? "?").slice(0, 1) || "?";
  return (
    <li className={styles.row}>
      {guest ? (
        <span className={styles.avatar} aria-hidden>
          {initial}
        </span>
      ) : rsvp.user?.image ? (
        <img className={styles.avatar} src={rsvp.user.image} alt="" />
      ) : (
        <span className={styles.avatar} aria-hidden>
          {initial}
        </span>
      )}
      <span className={styles.name}>{displayName}</span>
      {guest ? (
        <span className={styles.guestPill} aria-label="Guest RSVP">
          Guest
        </span>
      ) : null}
    </li>
  );
}
